import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Trans } from 'react-i18next'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { addApi, addUser } from 'shared/src/api'
import { isFeedItemMedia } from 'shared/src/api/types'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addCmsLoadingAndErrorIndicators } from '../../utils/addCmsLoadingAndErrorIndicators'
import { Page } from '../Page'
import {
  BUTTONS_STYLE,
  MAIN_TITLE_STYLE,
  NOTES_BOLD_STYLE,
  RODEO_E_STYLE,
} from '../styles'
import {
  makeAdminCreatorEarningsRoute,
  makeAdminCreatorInsightsRoute,
  makeNewPostRoute,
} from '../../utils/routes'
import { Media } from './Media'
import Icon from '../Icon'
import { Stat } from './Stat'

const useStyles = createUseStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 3,
  },
  title: MAIN_TITLE_STYLE,
  rodeoE: RODEO_E_STYLE,
  subtitle: {
    ...NOTES_BOLD_STYLE,
    color: '#90734F',
    opacity: 0.8,
    maxWidth: 150,
    marginBottom: 50,
  },
  statsContainer: {
    display: 'flex',
    marginBottom: 50,
    columnGap: 50,
  },
  media: {
    display: 'flex',
    flexDirection: 'column',
    gap: 100,
  },
  newButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    width: 138,
    backgroundColor: 'rgba(54, 52, 50, 0.96)',
    ...BUTTONS_STYLE,
    color: 'white',
  },
  newButtonIcon: {
    marginRight: 8,
  },
})

export const MyRodeo: FC = flowMax(
  addDisplayName('MyRodeo'),
  addNavigate,
  addUser,
  addApi,
  addOptionalIntPathParam('userId'),
  addDataLoadingFromPromise(
    'data',
    ({ api, userId, user }) =>
      Promise.all([
        userId ? api.users.feed(userId) : api.users.currentUserFeed(),
        api.creatorEarnings.creatorOverview(userId ?? user.id),
      ]),
    ['username'],
    ({ user: { username } }) => `myRodeo-feed-${username}`
  ),
  addCmsLoadingAndErrorIndicators('data', {
    redirectTo404IfError: true,
  }),
  addProps(({ data: [feed, earningsOverview] }) => ({
    feed,
    earningsOverview,
  })),
  addHandlers({
    onNewClick:
      ({ navigate, userId }) =>
      () => {
        navigate(makeNewPostRoute(userId))
      },
    onClickInsight:
      ({ navigate, userId }) =>
      () => {
        navigate(userId ? makeAdminCreatorInsightsRoute(userId) : '/insights')
      },
    onClickCurrentBalance:
      ({ navigate, userId }) =>
      () => {
        navigate(userId ? makeAdminCreatorEarningsRoute(userId) : '/earnings')
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    onNewClick,
    onClickInsight,
    onClickCurrentBalance,
    feed: { media, user },
    earningsOverview,
    classes,
    t,
  }) => (
    <Page>
      <div className={classes.header}>
        <h1 className={classes.title}>
          <Trans t={t} i18nKey="admin.myRodeo.title">
            <span className={classes.rodeoE} />
          </Trans>
        </h1>
        <button onClick={onNewClick} className={classes.newButtonContainer}>
          <Icon name="add" className={classes.newButtonIcon} />
          <span>{t('admin.myRodeo.new')}</span>
        </button>
      </div>
      <p className={classes.subtitle}>{t('admin.myRodeo.subtitle')}</p>
      {user.stats && (
        <div className={classes.statsContainer}>
          <button onClick={onClickInsight}>
            <Stat
              statValue={user.stats.mediaViews}
              statDescription={t('admin.myRodeo.totalViews')}
              type="number"
            />
          </button>
          {user.stats.brandMediaViews != null && (
            <Stat
              statValue={user.stats.brandMediaViews}
              statDescription={t('admin.myRodeo.brandMediaViews')}
              type="number"
            />
          )}
          {earningsOverview.currentBalance > 0 ? (
            <button onClick={onClickCurrentBalance}>
              <Stat
                statValue={earningsOverview.currentBalance}
                statDescription={t('admin.myRodeo.currentBalance')}
                type="dollar"
              />
            </button>
          ) : (
            <button onClick={onClickInsight}>
              <Stat
                statValue={user.stats.follows}
                statDescription={t('admin.myRodeo.followers')}
                type="number"
              />
            </button>
          )}
        </div>
      )}
      <div className={classes.media}>
        {media
          .flatMap((item) => (isFeedItemMedia(item) ? [item] : []))
          .map((media) => (
            <Media
              media={media}
              showEditButton={media.user.id === user.id}
              key={media.id}
            />
          ))}
      </div>
    </Page>
  )
)
